import { IconButton } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { format } from "date-fns";
import es from "date-fns/locale/es";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { sendMessage } from "../../../../api/ActionRequired";
import { HideGlobalLoading, ShowGlobalLoading } from "../../../../redux/actions/UiActions";
import { replaceGuionToSlashFromString } from "../../../../utilities/functions/StringUtil";
import COLORS from "../../../../theme/Colors";
import FONTS_FAMILY from "../../../../theme/FontsFamily";
import {
    StyledSendIcon,
} from "../../styles/ServiceRequestedDetailsStyles";
import { InformationFormSchema } from "./MessagesConstants";

const safeDecode = (s) => {
    if (typeof s !== "string") return "";
    try {
        return decodeURIComponent(s);
    } catch {
        return s;
    }
};

function Messages() {
    const matchesWidth = useMediaQuery("(min-width:768px)");
    const history = useHistory();
    let { requestID } = useParams();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const requestData = queryClient.getQueryData(["serviceRequestedDetail", requestID]);
    const userData = queryClient.getQueryData(["userData"]);

    const messageMutation = useMutation(sendMessage, {
        onMutate: (req) => {
            dispatch(ShowGlobalLoading("Cargando"));
        },
    });

    const textFormik = useFormik({
        initialValues: {
            information: "",
        },
        validationSchema: InformationFormSchema,
        onSubmit: (values) => {
            handleSubmit(values);
        },
    });

    const handleSubmit = (values) => {
        if (!values.information || !values.information.trim()) return;

        const requiredActionData = (requestData?.requiredActions && Array.isArray(requestData.requiredActions))
            ? requestData.requiredActions.find((action) => action.action_id === 3 || action.id)
            : null;

        const reqData = {
            request_id: requestData.request.id,
            text: values.information,
            required_action_id: requiredActionData?.id || requestData?.request?.request_actions_id || requestData?.request?.id,
        };

        messageMutation.mutate(reqData, {
            onSuccess: () => {
                enqueueSnackbar("Mensaje enviado satisfactoriamente", { variant: "success" });
                queryClient.invalidateQueries(["serviceRequestedDetail", requestID]);
                textFormik.resetForm();
            },
            onError: () => {
                enqueueSnackbar("Ha ocurrido un error al enviar el mensaje, contacte a soporte", { variant: "error" });
            },
            onSettled: () => {
                dispatch(HideGlobalLoading());
            },
        });
    };

    const messagesData = (requestData?.request?.comments || []).map((message) => {
        return {
            id: message.id,
            text: message.text,
            user_id: message?.user_id,
            isUserMessage: message?.user_id != 2 ? true : false,
            created_at: message.created_at,
        };
    });

    const statusId = Number(requestData?.request?.status?.id || requestData?.request?.status_id);
    const isClosedOrCanceled = statusId === 10 || statusId === 8;

    // 1. Hay mensaje / requerimiento emitido por el backend/analista
    const requiredActionData = (requestData?.requiredActions && Array.isArray(requestData.requiredActions))
        ? requestData.requiredActions.find((action) => Number(action.action_id) === 3 || action.id)
        : null;

    const hasBackendMessage = (requestData?.request?.comments || []).some(m => Number(m?.user_id) === 2 || Number(m?.user_id) === 0) ||
        requestData?.request?.request_actions_id == 3 ||
        Boolean(requiredActionData);

    // 2. El último mensaje registrado fue enviado por el usuario
    const lastMessage = messagesData.length > 0 ? messagesData[messagesData.length - 1] : null;
    const lastMessageIsFromUser = lastMessage ? lastMessage.isUserMessage : false;

    // 3. Comprobar si la acción requerida fue completada
    const isActionRequiredCompleted = requiredActionData ? (
        requiredActionData.status === true ||
        requiredActionData.status === 1 ||
        requiredActionData.status === 'true' ||
        requiredActionData.completed === true ||
        requiredActionData.isCompleted === true
    ) : false;

    // Solo se activa si el backend envió un mensaje, NO ha sido respondido, NO está completado y la solicitud está abierta
    const canReply = hasBackendMessage && !lastMessageIsFromUser && !isActionRequiredCompleted && !isClosedOrCanceled;

    return (
        <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            boxShadow: 'none',
        }}>
            {/* Header */}
            <div style={{
                padding: '18px 24px',
                borderBottom: '1px solid #f1f5f9',
                background: '#eef2ff',
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: '17px',
                    fontWeight: 700,
                    color: COLORS.secondary,
                    fontFamily: FONTS_FAMILY.bold.body,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                }}>
                    <span style={{ fontSize: '20px' }}>💬</span>
                    Historial de mensajes
                    {messagesData.length > 0 && (
                        <span style={{
                            fontSize: '12px',
                            background: COLORS.secondary,
                            color: '#fff',
                            borderRadius: '12px',
                            padding: '2px 10px',
                            fontWeight: 600,
                        }}>
                            {messagesData.length}
                        </span>
                    )}
                </h3>
            </div>

            {/* Messages Area */}
            <div style={{
                padding: '20px',
                height: '420px',
                maxHeight: '420px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                background: '#fafbfc',
            }}>
                {messagesData.length > 0 ? (
                    messagesData.map((message) => (
                        <div
                            key={message.id}
                            style={{
                                alignSelf: message.isUserMessage ? 'flex-end' : 'flex-start',
                                maxWidth: '75%',
                                borderRadius: message.isUserMessage ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                padding: '14px 18px',
                                backgroundColor: message.isUserMessage ? COLORS.secondary : '#ffffff',
                                color: message.isUserMessage ? '#ffffff' : COLORS.secondary,
                                border: message.isUserMessage ? 'none' : '1px solid #e2e8f0',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                            }}
                        >
                            <p style={{
                                margin: 0,
                                fontSize: '14px',
                                lineHeight: 1.5,
                                fontFamily: FONTS_FAMILY.regular.body,
                                wordBreak: 'break-word',
                            }}>
                                {safeDecode(message.text)}
                            </p>
                            <p style={{
                                margin: '6px 0 0',
                                fontSize: '11px',
                                opacity: 0.6,
                                fontFamily: FONTS_FAMILY.regular.body,
                                textAlign: 'right',
                            }}>
                                {format(new Date(replaceGuionToSlashFromString(message.created_at)), "dd 'de' MMMM yyyy hh:mm a", {
                                    locale: es,
                                })}
                            </p>
                        </div>
                    ))
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#94a3b8',
                    }}>
                        <span style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }}>💬</span>
                        <p style={{
                            margin: 0,
                            fontSize: '16px',
                            fontWeight: 600,
                            fontFamily: FONTS_FAMILY.bold.body,
                        }}>
                            No hay mensajes
                        </p>
                        <p style={{
                            margin: '4px 0 0',
                            fontSize: '13px',
                            fontFamily: FONTS_FAMILY.regular.body,
                        }}>
                            Los mensajes aparecerán aquí
                        </p>
                    </div>
                )}
            </div>

            {/* Input Area / Status Banners */}
            {canReply ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 20px',
                    borderTop: '1px solid #e2e8f0',
                    background: '#ffffff',
                }}>
                    <input
                        type="text"
                        placeholder="Escriba su respuesta al mensaje..."
                        maxLength={500}
                        id="information"
                        value={textFormik.values.information}
                        onChange={textFormik.handleChange}
                        onBlur={textFormik.handleBlur}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                textFormik.handleSubmit();
                            }
                        }}
                        style={{
                            flex: 1,
                            padding: '12px 18px',
                            borderRadius: '24px',
                            border: '1px solid #e2e8f0',
                            fontSize: '14px',
                            fontFamily: FONTS_FAMILY.regular.body,
                            outline: 'none',
                            background: '#f8fafc',
                            transition: 'border-color 0.2s ease',
                        }}
                        onFocus={(e) => e.target.style.borderColor = COLORS.secondary}
                        onBlurCapture={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                    <IconButton
                        onClick={() => textFormik.handleSubmit()}
                        style={{
                            background: COLORS.secondary,
                            color: '#fff',
                            width: '44px',
                            height: '44px',
                            transition: 'background 0.2s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.primary}
                        onMouseLeave={(e) => e.currentTarget.style.background = COLORS.secondary}
                    >
                        <StyledSendIcon style={{ fontSize: '20px', color: '#fff' }} />
                    </IconButton>
                </div>
            ) : (
                <div style={{
                    padding: '14px 20px',
                    borderTop: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    textAlign: 'center',
                    color: '#475569',
                    fontSize: '13px',
                    fontFamily: FONTS_FAMILY.regular.body,
                }}>
                    {isClosedOrCanceled ? (
                        <span>🚫 Esta solicitud se encuentra desestimada o cerrada, por lo que no es posible enviar nuevos mensajes.</span>
                    ) : (isActionRequiredCompleted || lastMessageIsFromUser) ? (
                        <span>✅ <b>Acción / Respuesta completada.</b> Ha respondido al requerimiento del analista. El área de mensajes se reabrirá cuando el analista envíe una nueva consulta.</span>
                    ) : (
                        <span>ℹ️ No hay requerimiento de mensaje pendiente por responder del analista.</span>
                    )}
                </div>
            )}
        </div>
    );
}

export default Messages;
